import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

function isPWA() {
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
  );
}

const sexualOptions = [
  'Male',
  'Female',
  'Non-Binary',
  'Trans-Man',
  'Trans-Woman',
  'No Preference'
];

function getEmojiForOption(option) {
  switch (option.toLowerCase()) {
    case 'male':
      return '♂';
    case 'female':
      return '♀';
    case 'non-binary':
      return '⚲';
    case 'trans-man':
      return '⚧';
    case 'trans-woman':
      return '⚦';
    default:
      return '⊘';
  }
}

export default function DatingFilterBottomSheet({
  userId,
  eventId,
  open,
  onClose,
}) {
  // Age range
  const [minAge, setMinAge] = useState(25);
  const [maxAge, setMaxAge] = useState(35);

  // Sexual preferences
  const [selectedSexualPrefs, setSelectedSexualPrefs] = useState([]);

  // Fetch preferences when the sheet opens
  useEffect(() => {
    if (!open) return;
    axios
      .get(`${process.env.REACT_APP_API_URL}/event-preferences?userId=${userId}&eventId=${eventId}`)
      .then((res) => {
        if (res.data) {
          setMinAge(res.data.ageRange.min);
          setMaxAge(res.data.ageRange.max);
          const pref = res.data.sexualPreference;

          // --- ADDED THIS BLOCK: Re-map DB-stored strings to match your capitalized `sexualOptions` ---
          if (pref?.toLowerCase() === 'no preference') {
            setSelectedSexualPrefs(['No Preference']);
          } else if (pref) {
            const parts = pref.split(',').map((s) => s.trim().toLowerCase());
            const corrected = parts.map((p) => {
              const match = sexualOptions.find(
                (opt) => opt.toLowerCase() === p
              );
              return match || p;
            });
            setSelectedSexualPrefs(corrected);
          } else {
            setSelectedSexualPrefs([]);
          }
          // ---------------------------------------------------------------------------
        }
      })
      .catch((err) => console.error('Error fetching preferences:', err));
  }, [open, userId, eventId]);

  const handleMinInputChange = (e) => {
    let val = parseInt(e.target.value, 10);
    if (isNaN(val)) val = 18;
    setMinAge(val <= maxAge ? val : maxAge);
  };

  const handleMaxInputChange = (e) => {
    let val = parseInt(e.target.value, 10);
    if (isNaN(val)) val = 80;
    setMaxAge(val >= minAge ? val : minAge);
  };

  function allOptions() {
    return sexualOptions.filter((opt) => opt.toLowerCase() !== 'no preference');
  }

  const toggleSexualOption = (option) => {
    setSelectedSexualPrefs((prev) => {
      const lowerOption = option.toLowerCase();
      if (lowerOption === 'no preference') {
        if (prev.some((x) => x.toLowerCase() === 'no preference')) {
          return [];
        }
        return ['No Preference'];
      } else {
        let newSelection = prev.filter((x) => x.toLowerCase() !== 'no preference');
        if (newSelection.includes(option)) {
          newSelection = newSelection.filter((x) => x !== option);
        } else {
          newSelection.push(option);
        }
        return newSelection;
      }
    });
  };

  const clearSexualPrefs = () => {
    setSelectedSexualPrefs([]);
  };

  const isValid = minAge >= 18 && maxAge >= minAge && selectedSexualPrefs.length > 0;

  const handleApply = async () => {
    const allIndiv = allOptions().map((o) => o.toLowerCase());
    const mappedSelected = selectedSexualPrefs.map((o) => o.toLowerCase());
    const sexualPreference =
      mappedSelected.length === allIndiv.length
        ? 'no preference'
        : mappedSelected.join(', ');

    const payload = {
      userId,
      eventId,
      ageRange: { min: minAge, max: maxAge },
      sexualPreference,
    };

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/event-preferences`, payload);
      onClose && onClose();
    } catch (err) {
      const errorMsg = err.response?.data?.error || '';
      if (errorMsg.includes('already exist')) {
        try {
          const res2 = await axios.get(
            `${process.env.REACT_APP_API_URL}/event-preferences?userId=${userId}&eventId=${eventId}`
          );
          await axios.patch(
            `${process.env.REACT_APP_API_URL}/event-preferences/${res2.data._id}`,
            payload
          );
        } catch (err2) {
          console.error('Error patching preferences:', err2);
        }
      } else {
        console.error('Error saving preferences:', err);
      }
      onClose && onClose();
    }
  };

  const sheetRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (sheetRef.current && !sheetRef.current.contains(e.target)) {
        onClose && onClose();
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  useEffect(() => {
    const themeMetaTag = document.querySelector('meta[name="theme-color"]');
  
    if (open) {
      document.body.classList.add('overlay-active');
      document.documentElement.classList.add('overlay-active');
      document.getElementById('root').classList.add('overlay-active');
      if (themeMetaTag) themeMetaTag.content = '#999998';
    } else {
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root').classList.remove('overlay-active');
      if (themeMetaTag) themeMetaTag.content = '#ffffff';
    }
  
    return () => {
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root').classList.remove('overlay-active');
      if (themeMetaTag) themeMetaTag.content = '#ffffff';
    };
  }, [open]);

  
if (!open) return null;



  const preferenceRows = [
    ['Male', 'Female'],
    ['Trans-Man', 'Trans-Woman'],
    ['Non-Binary', 'No Preference'],
  ];

  return (
    <div style={styles.backdrop}>
      <div style={styles.sheet} ref={sheetRef}>
        <div style={styles.sheetHeader}>
          <h3 style={{ margin: 0, fontWeight: 550 }}>Dating Filters</h3>
          <button type="button" onClick={clearSexualPrefs} style={styles.clearButton}>
            Clear
          </button>
        </div>

        <div style={styles.content}>
          {/* Updated Age Range Section: Two input groups side by side */}
          <div style={styles.ageRow}>
            <div style={styles.ageCard}>
              <label style={styles.ageCardLabel}>Min Age</label>
              <div style={styles.ageCardInner}>
                <input
                  type="number"
                  value={minAge}
                  onChange={handleMinInputChange}
                  style={styles.ageInput}
                />
                <div style={styles.buttonGroup}>
                  <button
                    type="button"
                    style={styles.iconButton}
                    onClick={() => setMinAge((prev) => (prev - 1 >= 18 ? prev - 1 : 18))}
                  >
                    –
                  </button>
                  <button
                    type="button"
                    style={styles.iconButton}
                    onClick={() => setMinAge((prev) => (prev + 1 <= maxAge ? prev + 1 : maxAge))}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
            <div style={styles.ageCard}>
              <label style={styles.ageCardLabel}>Max Age</label>
              <div style={styles.ageCardInner}>
                <input
                  type="number"
                  value={maxAge}
                  onChange={handleMaxInputChange}
                  style={styles.ageInput}
                />
                <div style={styles.buttonGroup}>
                  <button
                    type="button"
                    style={styles.iconButton}
                    onClick={() => setMaxAge((prev) => (prev - 1 >= minAge ? prev - 1 : minAge))}
                  >
                    –
                  </button>
                  <button
                    type="button"
                    style={styles.iconButton}
                    onClick={() => setMaxAge((prev) => (prev + 1 <= 80 ? prev + 1 : 80))}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sexual Preference Section */}
          <div style={styles.section}>
            <label style={styles.label}>Preferences</label>
            <div style={styles.prefGrid}>
              {preferenceRows.map((row, idx) => (
                <div style={styles.prefGridRow} key={idx}>
                  {row.map((option) => {
                    const isSelected = selectedSexualPrefs.includes(option);
                    return (
                      <div
                        key={option}
                        onClick={() => toggleSexualOption(option)}
                        style={{
                          ...styles.prefCard,
                          ...(isSelected ? styles.prefCardActive : {}),
                        }}
                      >
                        <div style={styles.prefIcon}>{getEmojiForOption(option)}</div>
                        <div style={styles.prefTitle}>{option}</div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={styles.footerRow}>
          <button onClick={() => onClose && onClose()} style={styles.cancelBtn}>
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!isValid}
            style={{
              ...styles.applyBtn,
              // CHANGED FROM backgroundColor TO background HERE:
              ...(isValid ? {} : { background: '#ccc', cursor: 'not-allowed' }),
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: 'fixed',
    zIndex: 9999,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  sheet: {
    width: '100%',
    maxWidth: 460,
    backgroundColor: '#fff',
    borderRadius: 12,
    boxShadow: '0 -2px 8px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    animation: 'slideUp 0.3s ease-out',
    fontFamily: "'Open Sans', sans-serif",
    margin: '8px',
    marginBottom: isPWA() ? 40 : 8, // conditional margin-bottom for PWA
    height: 570,
    position: 'relative',
  },
  sheetHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #eee',
    padding: '0.75rem 1rem',
    fontWeight: 600,
    fontSize: '.95rem',

  },
  clearButton: {
    padding: '6px 12px',
    border: 'none',
    borderRadius: 50,
    background: '#f0f0f0',
    color: '#333',
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
  },
  content: {
    padding: '1rem',
    paddingBottom: '80px',
    maxHeight: '60vh',
    overflowY: 'auto',
  },
  section: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: 8,
  },
  ageRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
  },
  ageCard: {
    flex: 1,
    borderRadius: 8,
    marginBottom:16,
  },
  ageCardLabel: {
    display: 'block',
    fontSize: '.85rem',
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#000',
    textAlign: 'left',
  },
  ageCardInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: '1px solid #ddd',
    borderRadius: 6,
    padding: '0.5rem .5rem',
    backgroundColor: '#fff',
  },
  ageInput: {
    border: 'none',
    fontSize: '1rem',
    fontWeight: 500,
    width: 60,
    outline: 'none',
    backgroundColor: '#fff',
    textAlign: 'left',
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  iconButton: {
    border: 'none',
    background: '#eee',
    fontSize: '1.2rem',
    color: '#000',
    cursor: 'pointer',
    width: 32,                  // Adjusted for perfect circle
    height: 32,                 // Adjusted for perfect circle
    borderRadius: '50%',        // Ensures circular shape
    display: 'flex',            // Center content horizontally and vertically
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,                 // Remove padding to prevent oval shape
    lineHeight: 1,              // Ensures exact vertical centering
  },
  
  prefGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginTop: '1rem',
  },
  prefGridRow: {
    display: 'flex',
    gap: '1rem',
  },
  prefCard: {
    flex: 1,
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    border: '1px solid #ddd',
    padding: '1rem',
    cursor: 'pointer',
    alignItems: 'left',
    justifyContent: 'left',
  },
  prefCardActive: {
    backgroundColor: '#EBF4FF',
    border: '1px solid #3399ff',
    color: '#000',
  },
  prefIcon: {
    fontSize: '1.3rem',
    marginBottom: '0.25rem',
  },
  prefTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  footerRow: {
    position: 'absolute',
    bottom: 10,
    left: 0,
    right: 0,
    display: 'flex',
    gap: 8,
    padding: '0.75rem 1rem',
    boxSizing: 'border-box',
  },
  cancelBtn: {
    flex: 1,
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    padding: '12px 0',
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
    maxWidth: 178,
    backgroundColor: '#f0f0f0', // <-- keeps the current grey background
    color: '#000',              // <-- explicitly sets black text  
  },
  applyBtn: {
    flex: 1,
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    padding: '12px 0',
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
    maxWidth: 178,
    background: 'linear-gradient(90deg, #3B82F6, #2563EB)',
    color: '#fff',
  },
  optionsContainer: {
    display: 'none',
  },
};
